/**
 * Cross-platform geolocation helper.
 *
 * On the web (and inside a normal PWA install), we fall back to the standard
 * `navigator.geolocation` API. Inside a Capacitor-wrapped native app, the
 * `@capacitor/geolocation` plugin gives us a higher-accuracy GPS fix because
 * it hits the OS APIs directly (CoreLocation / FusedLocationProvider) instead
 * of the browser-mediated API.
 *
 * Returns `{ latitude, longitude, accuracy }` (accuracy in metres) or throws.
 */

let cachedCap = null;
async function getCap() {
  // Lazy load — Capacitor is a runtime decision, not a compile-time one. On
  // the web build the plugin still exists but reports `Capacitor.isNativePlatform()`
  // as false, so we fall back to navigator.geolocation.
  if (cachedCap) return cachedCap;
  try {
    const cap = await import("@capacitor/core").then((m) => m.Capacitor);
    cachedCap = cap;
    return cap;
  } catch { return null; }
}

export async function getCurrentPosition({ highAccuracy = true, timeoutMs = 10000 } = {}) {
  const cap = await getCap();
  if (cap && cap.isNativePlatform && cap.isNativePlatform()) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy: highAccuracy,
      timeout: timeoutMs,
      maximumAge: 5000,
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
    };
  }
  // Web fallback — wrap the callback API in a promise.
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported on this device")); return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
      }),
      (err) => reject(err),
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 5000 },
    );
  });
}
